/**
 * SaveStore v1 (Appendix B) over localStorage, behind an interface so a cloud
 * backend can slot in later without refactor. Versioned schema + migration
 * hook chain (quality gate #5 exercises it).
 *
 * M1 uses only `settings`; levels/daily/cosmetics/ghosts fill in from M2 on.
 */

export interface LevelEntry {
  completed: boolean;
  bestTimeMs?: number;
  deathless: boolean;
  bits: [boolean, boolean, boolean];
}

export interface SaveV1 {
  version: 2;
  settings: {
    musicVol: number; // 0..1
    sfxVol: number; // 0..1
    latencyOffsetMs: number;
    quality: "auto" | "low" | "med" | "high";
    reducedMotion: boolean;
    controlVariant: "zones" | "swipe";
    /** R2: which touch throttle layout is active (SaveV2 addition). */
    throttleTouchLayout: "zones" | "slider";
    /** One-time touch-controls diagram shown? */
    touchHintShown: boolean;
  };
  levels: Record<string, LevelEntry>;
  /** One-time flow flags (tutorial gate). */
  progress: { tutorialDone: boolean };
  daily: {
    lastScoredDate?: string;
    streak: number;
    history: Array<{ date: string; progressPct: number; timeMs?: number; seedHash: string }>;
  };
  cosmetics: { unlocked: string[]; equippedShip: string; equippedTrail: string };
  ghosts: Record<string, string>; // levelId → serialized GhostRecord
  /** Ghost of today's scored Daily attempt (kept for that day's practice). */
  dailyGhost?: { date: string; ghost: string };
}

export function defaultSave(): SaveV1 {
  return {
    version: 2,
    settings: {
      musicVol: 0.8,
      sfxVol: 0.8,
      latencyOffsetMs: 0,
      quality: "auto",
      reducedMotion: false,
      controlVariant: "zones",
      throttleTouchLayout: "zones",
      touchHintShown: false,
    },
    levels: {},
    progress: { tutorialDone: false },
    daily: { streak: 0, history: [] },
    cosmetics: { unlocked: [], equippedShip: "default", equippedTrail: "trail-default" },
    ghosts: {},
  };
}

const KEY = "skypulse-save";
const CURRENT_VERSION = 2;

/** Each migration lifts version n → n+1. Registered as schemas evolve. */
export const MIGRATIONS: Record<number, (raw: Record<string, unknown>) => Record<string, unknown>> =
  {
    // v1 → v2 (R2): settings.throttleTouchLayout — the default-merge path
    // (D-015) fills the new key; the migration only lifts the version.
    1: (raw) => ({ ...raw, version: 2 }),
  };

export function migrate(raw: Record<string, unknown>): SaveV1 | null {
  let data = raw;
  let version = typeof data.version === "number" ? data.version : 0;
  while (version < CURRENT_VERSION) {
    const step = MIGRATIONS[version];
    if (!step) return null;
    data = step(data);
    version = typeof data.version === "number" ? (data.version as number) : version + 1;
  }
  if (data.version !== CURRENT_VERSION) return null;
  // Merge over defaults so missing keys (pre-release additions) self-heal.
  const base = defaultSave();
  const d = data as Partial<SaveV1>;
  return {
    ...base,
    ...d,
    version: 2,
    settings: { ...base.settings, ...(d.settings ?? {}) },
    progress: { ...base.progress, ...(d.progress ?? {}) },
    daily: { ...base.daily, ...(d.daily ?? {}) },
    cosmetics: { ...base.cosmetics, ...(d.cosmetics ?? {}) },
  };
}

export class SaveStore {
  private constructor(
    private data: SaveV1,
    private storage: Pick<Storage, "getItem" | "setItem">,
  ) {}

  static load(storage: Pick<Storage, "getItem" | "setItem"> = localStorage): SaveStore {
    let data: SaveV1 | null = null;
    try {
      const raw = storage.getItem(KEY);
      if (raw) data = migrate(JSON.parse(raw) as Record<string, unknown>);
    } catch {
      data = null; // corrupted save — start fresh rather than crash
    }
    return new SaveStore(data ?? defaultSave(), storage);
  }

  get settings(): SaveV1["settings"] {
    return this.data.settings;
  }

  updateSettings(patch: Partial<SaveV1["settings"]>): void {
    this.data.settings = { ...this.data.settings, ...patch };
    this.persist();
  }

  get tutorialDone(): boolean {
    return this.data.progress.tutorialDone;
  }

  markTutorialDone(): void {
    this.data.progress.tutorialDone = true;
    this.persist();
  }

  getLevel(id: string): LevelEntry | undefined {
    return this.data.levels[id];
  }

  get daily(): SaveV1["daily"] {
    return this.data.daily;
  }

  get cosmetics(): SaveV1["cosmetics"] {
    return this.data.cosmetics;
  }

  equipShip(id: string): void {
    this.data.cosmetics.equippedShip = id;
    this.persist();
  }

  getGhost(levelId: string): string | undefined {
    return this.data.ghosts[levelId];
  }

  setGhost(levelId: string, serialized: string): void {
    this.data.ghosts[levelId] = serialized;
    this.persist();
  }

  get dailyGhost(): { date: string; ghost: string } | undefined {
    return this.data.dailyGhost;
  }

  setDailyGhost(date: string, ghost: string): void {
    this.data.dailyGhost = { date, ghost };
    this.persist();
  }

  /**
   * Bank a finished run (spec 6.1 mastery grid): completion sticks, best time
   * improves monotonically, bits OR in ("collection persists once banked at
   * finish"), deathless sticks once earned.
   */
  recordFinish(
    id: string,
    result: { timeMs: number; bitsMask: number; deathless: boolean },
  ): { entry: LevelEntry; isNewBest: boolean } {
    const prev = this.data.levels[id];
    const isNewBest = prev?.bestTimeMs === undefined || result.timeMs < prev.bestTimeMs;
    const entry: LevelEntry = {
      completed: true,
      bestTimeMs: isNewBest ? result.timeMs : prev.bestTimeMs,
      deathless: (prev?.deathless ?? false) || result.deathless,
      bits: [
        (prev?.bits[0] ?? false) || (result.bitsMask & 1) !== 0,
        (prev?.bits[1] ?? false) || (result.bitsMask & 2) !== 0,
        (prev?.bits[2] ?? false) || (result.bitsMask & 4) !== 0,
      ],
    };
    this.data.levels[id] = entry;
    this.persist();
    return { entry, isNewBest };
  }

  persist(): void {
    try {
      this.storage.setItem(KEY, JSON.stringify(this.data));
    } catch {
      // storage full/blocked — play on, persistence is best-effort
    }
  }
}
